"use client";
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Image } from "antd";
import "./page.css";
import { useIntl } from "react-intl";
import { _eventBus } from "../../../core/events/EventBus";
import PopularTrips from "../../../Components/NetworkMaps/PopularTrips/PopularTrips";
import AvailableShops from "../../../Components/NetworkMaps/AvailableShops/AvailableShops";
import Section from "../../../Components/Section/Section";
import { useParams } from "next/navigation";
import { evtTree } from "../../../Orchestrator/ConstantTree";
import { useOnLanguageChange } from "../../../Components/CustomHooks/useOnLanguageChange";
import { useRecState } from "../../../Orchestrator/Recoils/Events/Recoil";
import {
  ICmsNetworkDetail,
  StationsListType,
} from "../../../Orchestrator/Types/Index";
import CommonCardSlider from "../../../Components/CommonCardSlider/CommonCardSlider";
import Loader from "../../../Components/Loader/Loader";
import {
  dayOrder,
  dayOrderArabic,
  getAmenitiesIconByName,
  convertTimeToArabic,
} from "../../../Lib/Utils/validationUtil";
import { Button } from "antd";
import LiveTrainScheduleTable, {
  TrainRecord,
  TrainStatus,
} from "../../../Components/LiveTrainSchedule/LiveTrainScheduleTable";
import { getTrainSchedule } from "../../api/getTrainSchedule";
import { isShuttleSlug } from "../../../Lib/Utils/stationGrouping";

const NetworkDetailPage = () => {
  const intl = useIntl();
  const [networkDetail] = useRecState<ICmsNetworkDetail>(
    "networkDetailResponseAtom"
  );
  const params = useParams();
  const slug = params.network_id;
  // Shuttle routes share this page but hide the train schedule and use
  // shuttle-specific section headings (SHUTTLE PAGE design)
  const isShuttle = isShuttleSlug(
    Array.isArray(slug) ? slug[0] : slug
  );
  const [initialLoadFinished, setInitialLoadFinished] = useState(false);
  const [stopsData] = useRecState<StationsListType>("stopsResponseAtom");
  const [visible, setVisible] = useState(false);
  const [trainScheduleLoading, setTrainScheduleLoading] = useState(false);
  const [trainSchedule, setTrainSchedule] = useState<TrainRecord[]>([]);
  const [trainScheduleError, setTrainScheduleError] = useState(false);
  const [scheduleEmptyMessage, setScheduleEmptyMessage] = useState<string | undefined>(undefined);
  const [scheduleLimit, setScheduleLimit] = useState(3);
  const [hasMoreTrains, setHasMoreTrains] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoadFinished(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    return () => {
      _eventBus.emit({
        eventName: "FLOW_CLOSE",
        data: "NetworkDetailFlow",
      })
    }
  }, []);

  useEffect(() => {
    if (slug) {
      const timer = setTimeout(() => {
        _eventBus.emit({
          eventName: evtTree.NetowrkDetail.Get,
          data: { slug },
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [slug]);

  useOnLanguageChange(() => {
    _eventBus.emit({
      eventName: evtTree.NetowrkDetail.Get,
      data: { slug },
    });
  });

  const onDirectionClick = () => {
    const lat = networkDetail?.station?.lat?.trim();
    const lng = networkDetail?.station?.lng?.trim();
    if (lat && lng) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      window.open(url, "_blank");
    }
  };

  const onBackClick = () => {
    _eventBus.emit({
      eventName: evtTree.NetowrkDetail.Back,
    });
  };

  const getDayIndex = (day: string) =>
    (intl.locale === "ar" ? dayOrderArabic : dayOrder).indexOf(day);

  const slots = (networkDetail?.station?.time_slot ?? []).map((slot) => ({
    day: slot.week_day?.name,
    opening_hrs: slot.opening_hrs?.slice(0, 5),
    closing_hrs: slot.closing_hrs?.slice(0, 5),
  }));

  // Group slots by time range key
  const groupedByTime: Record<string, string[]> = {};
  for (const { day, opening_hrs, closing_hrs } of slots) {
    if (!day) continue;
    const timeKey = `${opening_hrs}-${closing_hrs}`;
    if (!groupedByTime[timeKey]) groupedByTime[timeKey] = [];
    groupedByTime[timeKey].push(day);
  }

  const sortedTimeSlots = Object.entries(groupedByTime)
    .map(([time, days]) => ({
      time,
      days: Array.from(new Set(days)).sort(
        (a, b) => getDayIndex(a) - getDayIndex(b)
      ),
    }))
    .sort((a, b) => getDayIndex(a.days[0]) - getDayIndex(b.days[0]));

  const formatDayRanges = (days: string[]) => {
    const dayOrderArr = intl.locale === "ar" ? dayOrderArabic : dayOrder;
    const indices = days
      .map((day) => dayOrderArr.indexOf(day))
      .sort((a, b) => a - b);
    const ranges = [];
    let start = indices[0],
      end = indices[0];
    for (let i = 1; i <= indices.length; i++) {
      if (indices[i] === end + 1) {
        end = indices[i];
      } else {
        ranges.push({
          range:
            start === end
              ? dayOrderArr[start]
              : `${dayOrderArr[start]} - ${dayOrderArr[end]}`,
          firstDayIndex: start,
        });
        start = indices[i];
        end = indices[i];
      }
    }
    return ranges;
  };

  const allRanges = sortedTimeSlots.flatMap(({ time, days }) =>
    formatDayRanges(days).map((rangeObj) => ({
      ...rangeObj,
      time,
    }))
  );

  allRanges.sort((a, b) => a.firstDayIndex - b.firstDayIndex);

  const mapImageUrl =
    networkDetail?.station?.map_image?.formats?.medium?.url ||
    networkDetail?.station?.map_image?.url;

  // The CMS leaves `image` null on locales where the station photo was never
  // uploaded, so index defensively and drop the block entirely — a srcless
  // <img> renders its alt text at full box size. Not the shared cmsImageUrl()
  // helper: that tries
  // media.url before formats.large, and this page wants the large format first.
  const stationImageUrl =
    networkDetail?.station?.image?.[0]?.formats?.large?.url ||
    networkDetail?.station?.image?.[0]?.url;

  const mapSection = !!mapImageUrl && (
    <div className="map">
      <Image
        src={mapImageUrl}
        alt={intl.formatMessage(
          { id: "stationMapAlt" },
          { stationName: networkDetail?.station?.name }
        )}
        preview={false}
      />
    </div>
  );

  // With no photo the left column would be empty. Rather than stretch Location
  // and Hours across the full row — which upscales and crops the map — move the
  // map over to fill it. Shuttle pages keep their floor plan there instead.
  const isMapInImageColumn = !stationImageUrl && !isShuttle && !!mapImageUrl;

  const hasImageColumn =
    !!stationImageUrl ||
    (isShuttle && !!networkDetail?.floor_plan?.url) ||
    isMapInImageColumn;

  const matchStop = useMemo(() => {
    return stopsData.find((stop) => stop.id === networkDetail?.station?.code);
  }, [stopsData, networkDetail?.station?.code]);

  useEffect(() => {
    // Shuttle routes have no train schedule; skip the section's API entirely
    if (isShuttle || !networkDetail?.station?.code) return;

    let isMounted = true;

    const fetchTrainSchedule = async () => {
      setTrainScheduleLoading(true);
      setTrainScheduleError(false);
      const dateParam = new Date()
        .toISOString()
        .split("T")[0]
        .replace(/-/g, "");

      try {
        const response = await getTrainSchedule(
          dateParam,
          networkDetail.station.code,
          "",
          scheduleLimit,
          0
        );

        if (response.status !== "ok") {
          throw new Error("Error in Train schedule API");
        }

        const trains = response.answer?.trains ?? [];

        const mappedTrains = trains?.map((train, idx): TrainRecord | null => {
            if (!train.departure_time || !train.arrival_time || !train.trip_id) {
              return null;
            }

            let status: TrainStatus = "OnTime";
            if (train.status === "delayed") status = "Late";
            else if (train.status === "cancelled") status = "Cancelled";

            const scheduledDeparture = convertTimeToArabic(train.departure_time);
            const scheduledArrival = convertTimeToArabic(train.arrival_time);

            const estimatedDeparture = convertTimeToArabic(
              train.estimated_departure_time || train.departure_time
            );
            const estimatedArrival = convertTimeToArabic(
              train.estimated_arrival_time || train.arrival_time
            );

            let departure = "";
            let arrival = "";
            let originalDeparture: string | undefined = undefined;
            let originalArrival: string | undefined = undefined;

            if (status === "OnTime") {
              departure = scheduledDeparture;
              arrival = scheduledArrival;
            } else if (status === "Late") {
              originalDeparture = scheduledDeparture;
              originalArrival = scheduledArrival;
              departure = estimatedDeparture;
              arrival = estimatedArrival;
            } else if (status === "Cancelled") {
              originalDeparture = scheduledDeparture;
              originalArrival = scheduledArrival;
              departure = scheduledDeparture;
              arrival = scheduledArrival;
            }

            const details =
              status === "Cancelled"
                ? intl.formatMessage({ id: "scheduleCancelled" })
                : status === "Late"
                ? intl.formatMessage({ id: "scheduleLate" })
                : "";

            return {
              key: `${train.trip_id}_${idx}`,
              from: train.departure_station || "",
              to: train.arrival_station || "",
              departure,
              originalDeparture,
              arrival,
              originalArrival,
              trainNo: train.trip_short_name || train.trip_id,
              status,
              details,
            };
          })
          .filter((t): t is TrainRecord => t !== null)
          .sort((a, b) => a.departure.localeCompare(b.departure));

        if (!isMounted) return;

        setTrainSchedule(mappedTrains);

        // Server total (before paging); when absent, a full page means there
        // may be more, a short page means there aren't.
        const totalTrains =
          response.answer?.pagination?.total ?? response.answer?.total_trains;
        setHasMoreTrains(
          typeof totalTrains === "number"
            ? totalTrains > trains.length
            : trains.length >= scheduleLimit
        );

        const stationName = networkDetail?.station?.name || "";
        if (mappedTrains.length === 0) {
          setScheduleEmptyMessage(
            stationName
              ? intl.formatMessage(
                  { id: "noTrainsDepartingFrom" },
                  { stopName: stationName }
                )
              : intl.formatMessage({ id: "liveStatusUnavailable" })
          );
        } else {
          setScheduleEmptyMessage(undefined);
        }
      } catch (error) {
        console.error("Error fetching train schedule:", error);
        if (isMounted) {
          setTrainSchedule([]);
          setScheduleEmptyMessage(
            intl.formatMessage({ id: "liveStatusUnavailable" })
          );
          setTrainScheduleError(true);
        }
      } finally {
        if (isMounted) setTrainScheduleLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchTrainSchedule, 200);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [isShuttle, networkDetail?.station?.code, networkDetail?.station?.name, intl, scheduleLimit]);

  const getLocalizationKey = (locale: string) => {
    switch (locale) {
      case "fr":
        return "CULTURE.FR_FR";
      case "ar":
        return "CULTURE.AR_AE";
      case "en":
      default:
        return "CULTURE.EN_GB";
    }
  };

  // Shuttle pages show this directly under the banner image ("Station
  // Information"); train stations keep it in the row below ("Floor Plan")
  const floorPlanSection = !!networkDetail?.floor_plan?.url && (
    <div className={`floor-plan mb-40${isShuttle ? " mt-30" : ""}`}>
      <h4>
        {intl.formatMessage({
          id: isShuttle ? "stationInformation" : "floorPlan",
        })}
      </h4>
      <div className="direction-card floor-plan-card">
        <div className="floor-plan-wrapper">
          <Image
            src={networkDetail.floor_plan.url}
            alt={networkDetail.floor_plan.alternativeText || "Floor plan"}
            className="floor-image"
            preview={{
              visible,
              src: networkDetail.floor_plan.url,
              onVisibleChange: (value) => setVisible(value),
              getContainer: () => document.body,
              zIndex: 12000,
              mask: null,
            }}
          />
          <Button
            type="primary"
            icon={
              <Image
                src="/assets/network_icons/search-plus.svg"
                width={19.844}
                height={19.5}
                alt="Search"
                preview={false}
              />
            }
            className="zoom-button"
            onClick={() => setVisible(true)}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="network-detail-main">
        <div className="container">
          <div
            className="align-items-center mb-30 ff-inter"
            onClick={() => {
              onBackClick();
            }}
          >
            <div className="align-items-center cursor-pointer">
              <span className="mr-8 cursor-pointer d-flex back-btn-arrow">
                <Image
                  src="/assets/booking/arrow-left.svg"
                  width={19.498}
                  height={20.503}
                  preview={false}
                  alt="back"
                />
              </span>
              <span className="fs-16">
                {intl.formatMessage({ id: "backText" })}
              </span>
            </div>
          </div>
          {networkDetail?.station?.name ? (
            <>
              <h2 className="main-heading">{networkDetail?.station?.name}</h2>
              <Row gutter={[24, 0]} className="mb-40" align="top">
                {/* Station Image — dropped entirely when there is nothing to
                    put in it. Location and Hours stays at md=12 either way; the
                    residual half-row beats stretching it, which upscaled and
                    cropped the map. */}
                {hasImageColumn && (
                  <Col xs={24} sm={24} md={12}>
                    {stationImageUrl && (
                      <div className="station-img">
                        <Image
                          src={stationImageUrl}
                          preview={false}
                          height={380.98}
                          className="featured-image"
                          alt={networkDetail?.station?.name}
                        />
                      </div>
                    )}
                    {isShuttle && floorPlanSection}
                    {isMapInImageColumn && mapSection}
                  </Col>
                )}
                {/* Location and Hours */}
                <Col xs={24} sm={24} md={12}>
                  <div className="location-and-hrs">
                    <div className="location">
                      <h4>{intl.formatMessage({ id: "location" })}</h4>
                      <div className="direction-card location-card">
                        <div className="location-info">
                          <div className="address">
                            <span>
                              <Image
                                src="/assets/network_icons/location-pin.svg"
                                alt="city-img"
                                width={23}
                                height={26}
                                preview={false}
                              />
                            </span>
                            {networkDetail?.station?.address}
                          </div>
                          <button
                            className="direction"
                            onClick={() => {
                              onDirectionClick();
                            }}
                          >
                            <span>
                              <Image
                                src="/assets/network_icons/directions.svg"
                                alt="direction-img"
                                width={16}
                                height={15}
                                preview={false}
                              />
                            </span>{" "}
                            {intl.formatMessage({ id: "directions" })}
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Opening Hours Section */}
                    <div className="opening-hours">
                      <h4>
                        {intl.formatMessage({
                          id: isShuttle ? "operatingHours" : "opening_hours",
                        })}
                      </h4>
                      <div className="direction-card show-time">
                        <span className="time-icon">
                          <Image
                            src="/assets/network_icons/clock.svg"
                            width={29}
                            height={29}
                            alt="clock"
                            preview={false}
                          />
                        </span>
                        <div className="time-details">
                          {allRanges.length > 0 ? (
                            allRanges.map((rangeObj, idx) => (
                              <div
                                className="time"
                                key={rangeObj.time + rangeObj.range + idx}
                              >
                                <span>{rangeObj.range}</span>
                                <span>{rangeObj.time.replace("-", " - ")}</span>
                              </div>
                            ))
                          ) : (
                            <div className="text-center">
                              {intl.formatMessage({ id: "noData" })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {!isMapInImageColumn && mapSection}
                  </div>
                </Col>
              </Row>
            </>
          ) : initialLoadFinished ? (
            <div className="text-center">
              {intl.formatMessage({ id: "noData" })}
            </div>
          ) : (
            <Loader />
          )}

          {/* Shuttle pages show Station Information under the banner instead
              and have no amenities (SHUTTLE PAGE design) */}
          {!isShuttle && !!(networkDetail?.floor_plan?.url || matchStop) && (
            <Row gutter={[24, 0]} align="top">
              <Col xs={24} sm={24} md={12}>
                {floorPlanSection}
            </Col>

            <Col xs={24} sm={24} md={12} className="mb-40">
                {
                  matchStop &&
                  <div className="amenities">
                    <h4>{intl.formatMessage({ id: "amenities" })}</h4>
                    <div className="direction-card amenities-card">
                      <ul className="amenities-list">
                        {matchStop?.attributes?.map((attr) => {
                          const locKey = getLocalizationKey(intl.locale);
                          const label =
                            attr.localizations?.[locKey] || attr.name;
                          // Map code or name to an icon file
                          const iconSrc = getAmenitiesIconByName(attr.code);
                          return (
                            <li key={attr.code}>
                              {iconSrc && (
                                <span>
                                  <Image
                                    src={iconSrc}
                                    width={24}
                                    height={24}
                                    alt={label}
                                    preview={false}
                                    onError={(e) => {
                                      e.currentTarget.src = "";
                                    }}
                                  />
                                </span>
                              )}
                              {label}
                            </li>
                          );
                        }) || <li>{intl.formatMessage({ id: "noData" })}</li>
                      }
                  </ul>
                </div>
              </div>
                }
            </Col>
          </Row>
          )}

          {networkDetail?.shops && networkDetail?.shops?.length > 0 && (
            <Section type="today_trains_leaving_from">
              <div className="destination_slider thumbnail_slider">
                <h2>{intl.formatMessage({ id: "availableShops" })}</h2>
              </div>
              <CommonCardSlider
                title={""}
                items={networkDetail?.shops}
                slidesPerPage={6}
                noRecordText={intl.formatMessage({ id: "noData" })}
                renderItem={(shop) => <AvailableShops shop={shop} />}
                cardMinWidth={202}
                cardMaxWidth={380}
              />
            </Section>
          )}

          {!isShuttle &&
            networkDetail?.station &&
            networkDetail?.station?.code && (
            <Section type="today_trains_leaving_from">
              <div className="destination_slider thumbnail_slider">
                <div className="page-heading d-flex justify-content-between align-items-center">
                  <h2>{intl.formatMessage({ id: "trainSchedule" })}</h2>
                  {!trainScheduleError && (
                    <button
                      className="btn_link"
                      onClick={() => setScheduleLimit(9999)}
                      disabled={
                        !hasMoreTrains ||
                        scheduleLimit === 9999 ||
                        trainScheduleLoading
                      }
                    >
                      {intl.formatMessage({ id: "seeMoreText" })}
                    </button>
                  )}
                </div>
              </div>
              {trainScheduleLoading ? (
                <div style={{ padding: '40px 0', display: 'flex', justifyContent: 'center' }}>
                  <Loader />
                </div>
              ) : (
                <LiveTrainScheduleTable
                  data={trainSchedule}
                  emptyMessage={scheduleEmptyMessage}
                />
              )}
            </Section>
          )}

          {networkDetail?.popular_trips &&
            networkDetail?.popular_trips?.length > 0 && (
              <Section type="today_trains_leaving_from">
                <div className="destination_slider thumbnail_slider">
                  <h2>{intl.formatMessage({ id: "popularTripsText" })}</h2>
                </div>
                <CommonCardSlider
                  title={""}
                  items={networkDetail?.popular_trips}
                  slidesPerPage={3}
                  cardMinWidth={350}
                  cardMaxWidth={700}
                  noRecordText={intl.formatMessage({ id: "noData" })}
                  renderItem={(trip) => (
                    <PopularTrips
                      trip={trip}
                      stationCode={networkDetail?.station?.code}
                      stationName={networkDetail?.station?.name}
                    />
                  )}
                />
              </Section>
            )}
        </div>
      </div>
    </>
  );
};

export default NetworkDetailPage;
